import type {StarterPrompt} from '../assistant';

export interface StarterContext {
    pathname: string;
    /** Administrator: sets up the workspace. */
    isAdministrator: boolean;
    /** Administrator or maintainer: manages people, teams, job roles and tags. */
    canManageDirectory: boolean;
}

export const MAX_STARTERS = 4;

const SET_UP: StarterPrompt = {
    label: 'Help me set up my workspace',
    prompt: 'Help me get our Certavue workspace set up. Check what is already done and tell me the best next step.'
};

const NEW_HERE: StarterPrompt = {
    label: 'What can I do in Certavue?',
    prompt: 'I am new to Certavue. What can I do here, where do I find my work, and where should I start?'
};

const EXPLAIN_PAGE: StarterPrompt = {
    label: 'Explain this page',
    prompt: 'Explain what this page shows and what I can do here.'
};

const NEXT_STEP: StarterPrompt = {
    label: 'What should I do next?',
    prompt: 'Look at how far we are with getting started and tell me the next thing to do.'
};

interface PageStarters {
    prefix: string;
    starters: (context: StarterContext) => StarterPrompt[];
}

// Most specific prefix first: the first match wins.
const PAGE_STARTERS: PageStarters[] = [
    {prefix: '/getting-started', starters: () => [NEXT_STEP]},
    {
        prefix: '/settings/capacity/working-hours',
        starters: () => [
            {
                label: 'What should our working hours be?',
                prompt: 'Explain how working hours affect capacity in Certavue and help me decide what ours should be.'
            }
        ]
    },
    {
        prefix: '/settings/integrations',
        starters: () => [
            {
                label: 'Which way should I bring data in?',
                prompt: 'Compare the ways to get our data into Certavue (spreadsheet import, directory sync, API, the assistant) and recommend one for us.'
            }
        ]
    },
    {prefix: '/settings', starters: () => []},
    {
        prefix: '/dashboard',
        starters: () => [
            {
                label: 'Who is overloaded soon?',
                prompt: 'Who is overloaded in the next four weeks, and why?'
            }
        ]
    },
    {
        prefix: '/projects',
        starters: context =>
            context.canManageDirectory
                ? [
                      {
                          label: 'Help me create a project',
                          prompt: 'Help me create a project and staff it.'
                      },
                      {
                          label: 'Load projects from a spreadsheet',
                          prompt: 'I have a spreadsheet of projects. Help me load it into Certavue.'
                      }
                  ]
                : [
                      {
                          label: 'Which projects am I on?',
                          prompt: 'Which projects am I on, and how many hours a week am I booked?'
                      }
                  ]
    },
    {
        prefix: '/people',
        starters: context =>
            context.canManageDirectory
                ? [
                      {
                          label: 'Load people from a spreadsheet',
                          prompt: 'I have a spreadsheet of our people. Help me load it into Certavue.'
                      },
                      {
                          label: 'How do I record leave?',
                          prompt: 'How do I record someone\'s leave or reduced availability?'
                      }
                  ]
                : []
    },
    {
        prefix: '/teams',
        starters: () => [
            {
                label: 'How are my teams loaded?',
                prompt: 'How loaded are the teams over the next few weeks?'
            }
        ]
    },
    {
        prefix: '/insights',
        starters: () => [
            {
                label: 'Which insight answers my question?',
                prompt: 'Which insight should I use to understand where we are short of people?'
            }
        ]
    }
];

/**
 * The suggestions an empty assistant conversation offers: getting set up (or
 * finding your way, for people who don't run the workspace), something useful
 * for the page, and "explain this page". At most four, no duplicates.
 */
export const assistantStarters = (
    context: StarterContext
): StarterPrompt[] => {
    const page = PAGE_STARTERS.find(entry =>
        context.pathname.startsWith(entry.prefix)
    );
    const opener = context.canManageDirectory ? SET_UP : NEW_HERE;
    const candidates = [
        opener,
        ...(page?.starters(context) ?? []),
        EXPLAIN_PAGE
    ];

    const seen = new Set<string>();
    return candidates
        .filter(starter => {
            if (seen.has(starter.label)) return false;
            seen.add(starter.label);
            return true;
        })
        .slice(0, MAX_STARTERS);
};
